import {
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import type { ArdoqId, ViewIds } from '@ardoq/api-types';
import type {
  GlobalReferenceType,
  OnViewSettingsUpdate,
} from '@ardoq/data-model';
import { MenuMetaOptions, OptionIsActiveArgs } from './types';
import type {
  ReferenceTypeOption,
  TraversedReferenceTypeInfo,
} from './referenceTypeOption';
import { get, noop } from 'lodash';
import * as settingsBarConsts from '../consts';
import { isTruthy } from '@ardoq/common-helpers';
import { CHILDREN, PARENTS, CommonDropdownOptions } from '@ardoq/global-consts';
import { logError } from '@ardoq/logging';
import { ReactNode } from 'react';
import { type I18nLocale, t } from '@ardoq/i18n';

const INCLUDE_OUTGOING_REFERENCE_TYPE_NAMES =
  'includeOutgoingReferenceTypeNames';
const INCLUDE_INCOMING_REFERENCE_TYPE_NAMES =
  'includeIncomingReferenceTypeNames';

type CreateDropdownOptionArgs<
  T = string,
  TViewState = Record<
    string,
    string[] | string | (CommonDropdownOptions | TraversedReferenceTypeInfo)[]
  >,
> = {
  name: string;
  value: T;
  label: ReactNode;
  viewId: ViewIds;
  viewState: TViewState;
  key: keyof TViewState & string;
  availableOptions: T[];
  metaOptions?: MenuMetaOptions;
  deselectedByDefault?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  settingsPath?: string[];
  onViewSettingsUpdate?: OnViewSettingsUpdate;
  quickPerspectiveReferenceTypeFilters?: Set<string> | null;
  truncateLabel?: boolean;
};

const dropdownOptionIsActive = <
  T = string,
  TViewState = Record<
    string,
    string[] | string | (CommonDropdownOptions | TraversedReferenceTypeInfo)[]
  >,
>({
  viewState,
  allValues,
  key,
  settingsPath,
  value,
  deselectedByDefault,
}: OptionIsActiveArgs<T, TViewState>) => {
  const settingsKey = [...(settingsPath || []), key];
  const settingsValue = get(viewState, settingsKey);
  if (!settingsValue) {
    logError(Error('missing settings value in dropdownOptionIsActive.'));
    return false;
  }
  if (typeof value === 'string') {
    if (value === CommonDropdownOptions.NONE) {
      return (
        !settingsValue.includes(CommonDropdownOptions.ALL) &&
        !settingsValue.includes(
          CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD
        ) &&
        !allValues.some(value => settingsValue.includes(value))
      );
    }

    if (
      value === CommonDropdownOptions.ALL ||
      value === CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD
    ) {
      return (
        settingsValue.includes(value) ||
        allValues.every(value => settingsValue.includes(value))
      );
    }
  }
  if (
    settingsValue.includes(
      CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT
    ) &&
    deselectedByDefault
  ) {
    return settingsValue.includes(value);
  }
  return (
    settingsValue.includes(value) ||
    settingsValue.includes(CommonDropdownOptions.ALL) ||
    settingsValue.includes(
      CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT
    ) ||
    settingsValue.includes(CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD)
  );
};
const toggleField = <T = string>(
  availableOptions: any[],
  selectedOptions: any[],
  value: T
) => {
  if (typeof value === 'string') {
    if (
      value === CommonDropdownOptions.ALL ||
      value === CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD
    ) {
      return selectedOptions.includes(value) ? [] : [value];
    }
    if (value === CommonDropdownOptions.NONE) {
      return [];
    }
  }
  if (
    selectedOptions.includes(CommonDropdownOptions.ALL) ||
    selectedOptions.includes(CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD) ||
    selectedOptions.includes(
      CommonDropdownOptions.ALL_EXCEPT_DESELECTED_BY_DEFAULT
    )
  ) {
    return availableOptions.filter(selectedOption => selectedOption !== value);
  }

  if (selectedOptions.includes(value)) {
    return selectedOptions.filter(selectedOption => selectedOption !== value);
  }
  return [...selectedOptions, value];
};

export const createDropdownOption = <
  T = string,
  TViewState = Record<
    string,
    string[] | string | (CommonDropdownOptions | TraversedReferenceTypeInfo)[]
  >,
>({
  name,
  label,
  value,
  viewId,
  viewState,
  key,
  settingsPath,
  availableOptions,
  metaOptions = MenuMetaOptions.ALL_AND_NONE,
  deselectedByDefault,
  onClick,
  onViewSettingsUpdate = noop,
  isActive = dropdownOptionIsActive({
    viewState,
    allValues:
      metaOptions & MenuMetaOptions.ALL_EXCEPT_PARENT_CHILD
        ? availableOptions
        : [...availableOptions, CommonDropdownOptions.PARENT_AS_REFERENCE],
    key,
    settingsPath,
    value,
    deselectedByDefault,
  }),
  quickPerspectiveReferenceTypeFilters = null,
  truncateLabel = true,
}: CreateDropdownOptionArgs<T, TViewState>): DropdownOption => {
  const settingsKey = [...(settingsPath || []), key];
  const selectedOptions = get(viewState, settingsKey) as string[];

  const handleViewSettingsUpdate = () => {
    if (!onViewSettingsUpdate) return;

    const newValue = toggleField(
      metaOptions & MenuMetaOptions.ALL_EXCEPT_PARENT_CHILD
        ? availableOptions
        : [...availableOptions, CommonDropdownOptions.PARENT_AS_REFERENCE],
      selectedOptions,
      value
    );
    const settings = { [key]: newValue };
    const persistent = true;

    onViewSettingsUpdate(viewId, settings, persistent, settingsPath);
  };

  const isDisabled = quickPerspectiveReferenceTypeFilters
    ? !quickPerspectiveReferenceTypeFilters.has(name)
    : false;

  const result: DropdownOption = {
    name,
    label,
    onClick: onClick || handleViewSettingsUpdate,
    type: DropdownOptionType.OPTION,
    isActive: isDisabled ? false : isActive,
    isDisabled,
    truncateLabel,
  };
  return result;
};

type CreateAllNoneOptionArgs = {
  label: string;
  value: CommonDropdownOptions;
};
type GetAllNoneGroupArgs<T, TViewState> = {
  viewId: ViewIds;
  viewState: TViewState;
  key: keyof TViewState & string;
  availableOptions: T[];
  settingsPath?: string[];
  onClick?: (value: CommonDropdownOptions) => void;
  isActive?: (value: CommonDropdownOptions) => boolean;
  metaOptions?: MenuMetaOptions;
  onViewSettingsUpdate?: OnViewSettingsUpdate;
  i18nLocale: I18nLocale;
};
export const getAllNoneDropdownGroup = <
  T = string,
  TViewState = Record<
    string,
    string[] | string | (CommonDropdownOptions | TraversedReferenceTypeInfo)[]
  >,
>({
  viewId,
  viewState,
  key,
  availableOptions,
  settingsPath,
  onClick,
  isActive,
  metaOptions = MenuMetaOptions.ALL_AND_NONE,
  onViewSettingsUpdate,
  i18nLocale,
}: GetAllNoneGroupArgs<T, TViewState>) => {
  const createMetaOption = ({ label, value }: CreateAllNoneOptionArgs) =>
    createDropdownOption<T | CommonDropdownOptions, TViewState>({
      name: label,
      label,
      value,
      viewId,
      viewState,
      key,
      settingsPath,
      availableOptions,
      metaOptions,
      isActive: isActive?.(value),
      onClick: onClick ? () => onClick(value) : undefined,
      onViewSettingsUpdate,
    });
  const showAllLabel = t(
    i18nLocale,
    'settingsBar:showAll',
    settingsBarConsts.SHOW_ALL
  );
  const showNoneLabel = t(
    i18nLocale,
    'settingsBar:showNone',
    settingsBarConsts.SHOW_NONE
  );
  return [
    (metaOptions & MenuMetaOptions.ALL_OPTION) > 0 &&
      createMetaOption({
        value: CommonDropdownOptions.ALL,
        label: showAllLabel,
      }),
    (metaOptions & MenuMetaOptions.ALL_EXCEPT_PARENT_CHILD) > 0 &&
      createMetaOption({
        value: CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD,
        label: showAllLabel,
      }),
    (metaOptions & MenuMetaOptions.NONE_OPTION) > 0 &&
      createMetaOption({
        value: CommonDropdownOptions.NONE,
        label: showNoneLabel,
      }),
  ].filter(isTruthy);
};

export type ReferenceTypeInfoInput = {
  referenceType: GlobalReferenceType | null;
  linkedWorkspaceId: ArdoqId;
};

export const getReferenceTypeInfo = ({
  referenceType,
  linkedWorkspaceId,
}: ReferenceTypeInfoInput) => {
  const [referenceWorkspaceId, referenceTypeId] = referenceType?.id?.split(
    '-'
  ) || [undefined, undefined];
  return {
    referenceWorkspaceId,
    referenceTypeId,
    referenceTypeName: referenceType?.name ?? '',
    linkedWorkspaceId,
  };
};

type OptionGroupArgs<
  T = string,
  TViewState = Record<
    string,
    string | string[] | (CommonDropdownOptions | TraversedReferenceTypeInfo)[]
  >,
> = {
  viewId: ViewIds;
  viewState: TViewState;
  key: keyof TViewState & string;
  availableRefTypes: T[];
  metaOptions?: MenuMetaOptions;
  onClick?: (value: T) => void;
  isActive?: (value: T) => boolean;
  formatValue?: (value: T) => ReactNode;
  settingsPath?: string[];
  onViewSettingsUpdate?: OnViewSettingsUpdate;
  quickPerspectiveReferenceTypeFilters?: Set<string> | null;
  truncateLabel?: boolean;
  i18nLocale: I18nLocale;
};
export const getDropdownOptionGroup = <
  T = string,
  TViewState = Record<string, string | string[] | ReferenceTypeOption[]>,
>({
  viewState,
  key,
  viewId,
  availableRefTypes,
  metaOptions,
  isActive,
  onClick,
  formatValue,
  settingsPath,
  onViewSettingsUpdate,
  quickPerspectiveReferenceTypeFilters,
  truncateLabel,
  i18nLocale,
}: OptionGroupArgs<T, TViewState>) => {
  const referenceTypeGroup = availableRefTypes.map(refType => {
    const formattedValue = formatValue?.(refType);
    const name =
      typeof formattedValue === 'string' ? formattedValue : `${refType}`;
    return createDropdownOption<T, TViewState>({
      name,
      label: formattedValue ?? name,
      value: refType,
      viewId,
      viewState,
      key,
      settingsPath,
      availableOptions: availableRefTypes,
      metaOptions,
      isActive: isActive ? isActive(refType) : undefined,
      onClick: onClick ? () => onClick(refType) : undefined,
      onViewSettingsUpdate,
      quickPerspectiveReferenceTypeFilters,
      truncateLabel,
    });
  });

  const hasRefTypeOptions = referenceTypeGroup.length > 0;

  const allNoneGroup =
    hasRefTypeOptions && metaOptions
      ? getAllNoneDropdownGroup({
          viewId,
          viewState,
          key,
          availableOptions: availableRefTypes,
          settingsPath,
          metaOptions,
          onViewSettingsUpdate,
          i18nLocale,
        })
      : [];

  return {
    hasRefTypeOptions,
    referenceTypeGroup,
    allNoneGroup,
  };
};

const getParentsOrChildrenDropdownOption = <T = string>(
  viewId: ViewIds,
  viewState: Record<string, string[]>,
  showDivider: boolean,
  label: string,
  availableRefTypes: T[],
  settingsKey: string,
  onViewSettingsUpdate?: OnViewSettingsUpdate,
  settingsPath?: string[]
) =>
  [
    showDivider && {
      type: DropdownOptionType.DIVIDER,
    },
    createDropdownOption<T | CommonDropdownOptions>({
      name: CommonDropdownOptions.PARENT_AS_REFERENCE,
      label,
      value: CommonDropdownOptions.PARENT_AS_REFERENCE,
      viewId,
      viewState,
      key: settingsKey,
      settingsPath,
      availableOptions: availableRefTypes,
      onViewSettingsUpdate,
    }),
    showDivider && {
      type: DropdownOptionType.DIVIDER,
    },
  ].filter(isTruthy);
type ParentChildGroupArgs<T = string> = {
  viewId: ViewIds;
  viewState: Record<string, string[]>;
  availableOutgoingRefTypes: T[];
  availableIncomingRefTypes: T[];
  hasOutgoingRefTypeOptions: boolean;
  hasIncomingRefTypeOptions: boolean;
  isParentRelationAsReference: boolean;
  settingsPath?: string[];
  onViewSettingsUpdate?: OnViewSettingsUpdate;
};
const getParentsChildrenDropdownOptions = <T = string>({
  viewState,
  viewId,
  availableOutgoingRefTypes,
  availableIncomingRefTypes,
  hasOutgoingRefTypeOptions,
  hasIncomingRefTypeOptions,
  isParentRelationAsReference,
  settingsPath,
  onViewSettingsUpdate,
}: ParentChildGroupArgs<T>) =>
  isParentRelationAsReference
    ? {
        childrenOption: getParentsOrChildrenDropdownOption(
          viewId,
          viewState,
          hasOutgoingRefTypeOptions,
          CHILDREN,
          availableOutgoingRefTypes,
          INCLUDE_OUTGOING_REFERENCE_TYPE_NAMES,
          onViewSettingsUpdate,
          settingsPath
        ),
        parentsOption: getParentsOrChildrenDropdownOption(
          viewId,
          viewState,
          hasIncomingRefTypeOptions,
          PARENTS,
          availableIncomingRefTypes,
          INCLUDE_INCOMING_REFERENCE_TYPE_NAMES,
          onViewSettingsUpdate,
          settingsPath
        ),
      }
    : { childrenOption: [], parentsOption: [] };

type GetReferenceTypeOptionsArgs<T = string> = {
  viewId: ViewIds;
  availableOutgoingRefTypes: T[];
  availableIncomingRefTypes: T[];
  viewState: Record<string, any>;
  isParentRelationAsReference?: boolean;
  metaOptions?: MenuMetaOptions;
  isActive?: (value: T) => boolean;
  onClick?: (value: T) => void;
  formatValue?: (value: T) => ReactNode;
  settingsPath?: string[];
  onViewSettingsUpdate?: OnViewSettingsUpdate;
  quickPerspectiveReferenceTypeFilters?: Set<string> | null | undefined;
  truncateLabel?: boolean;
  i18nLocale: I18nLocale;
};
export const getReferenceTypeDropdownOptions = <T = string>({
  viewId,
  availableOutgoingRefTypes,
  availableIncomingRefTypes,
  viewState,
  isParentRelationAsReference = false,
  metaOptions = MenuMetaOptions.ALL_AND_NONE,
  isActive,
  onClick,
  formatValue,
  settingsPath,
  onViewSettingsUpdate,
  quickPerspectiveReferenceTypeFilters,
  truncateLabel,
  i18nLocale,
}: GetReferenceTypeOptionsArgs<T>) => {
  const {
    hasRefTypeOptions: hasOutgoingRefTypeOptions,
    referenceTypeGroup: outgoingRefTypes,
    allNoneGroup: outgoingAllNone,
  } = getDropdownOptionGroup({
    viewId,
    viewState,
    key: INCLUDE_OUTGOING_REFERENCE_TYPE_NAMES,
    settingsPath,
    availableRefTypes: availableOutgoingRefTypes,
    metaOptions,
    isActive,
    onClick,
    formatValue,
    onViewSettingsUpdate,
    quickPerspectiveReferenceTypeFilters,
    truncateLabel,
    i18nLocale,
  });

  const {
    hasRefTypeOptions: hasIncomingRefTypeOptions,
    referenceTypeGroup: incomingRefTypes,
    allNoneGroup: incomingAllNone,
  } = getDropdownOptionGroup({
    viewId,
    viewState,
    key: INCLUDE_INCOMING_REFERENCE_TYPE_NAMES,
    settingsPath,
    availableRefTypes: availableIncomingRefTypes,
    metaOptions,
    isActive,
    onClick,
    formatValue,
    onViewSettingsUpdate,
    quickPerspectiveReferenceTypeFilters,
    truncateLabel,
    i18nLocale,
  });

  const { childrenOption, parentsOption } = getParentsChildrenDropdownOptions({
    viewId,
    viewState,
    availableOutgoingRefTypes,
    availableIncomingRefTypes,
    hasOutgoingRefTypeOptions,
    hasIncomingRefTypeOptions,
    isParentRelationAsReference,
    settingsPath,
    onViewSettingsUpdate,
  });

  const result: DropdownItem[] = [
    (childrenOption.length > 0 || hasOutgoingRefTypeOptions) && {
      label: t(
        i18nLocale,
        'settingsBar:outgoingReferenceTypes',
        settingsBarConsts.OUTGOING_REFERENCE_TYPES
      ),
      type: DropdownOptionType.HEADER,
    },
    ...outgoingAllNone,
    ...childrenOption,
    ...outgoingRefTypes,
    (parentsOption.length > 0 || hasIncomingRefTypeOptions) && {
      label: t(
        i18nLocale,
        'settingsBar:incomingReferenceTypes',
        settingsBarConsts.INCOMING_REFERENCE_TYPES
      ),
      type: DropdownOptionType.HEADER,
    },
    ...incomingAllNone,
    ...parentsOption,
    ...incomingRefTypes,
  ].filter(isTruthy);
  return result;
};

export const formatReferenceType = (
  referenceType: TraversedReferenceTypeInfo
) => referenceType.displayName;
